import { useEffect, useState, type ChangeEvent, type RefObject } from "react";
import type {
  CustomPostAppearance,
  MeResponse,
  UploadedMedia
} from "../../api/client";
import {
  effectiveFormatForEditor
} from "./appearance-model";
import { ButtonLayoutEditor } from "./ButtonLayoutEditor";
import type { ButtonLayoutLimits, EditableButtonRow } from "./button-layout-model";

type AppearanceEditorProps = {
  capabilities: MeResponse["capabilities"];
  appearance: CustomPostAppearance;
  onAppearanceChange: (value: CustomPostAppearance) => void;
  buttonRows: EditableButtonRow[];
  onButtonRowsChange: (value: EditableButtonRow[]) => void;
  buttonLimits: ButtonLayoutLimits;
  buttonSetStatus: string | null;
  onChooseButtonSet: () => void;
  mediaItems: UploadedMedia[];
  mediaBusy: boolean;
  mediaError: string | null;
  mediaInputRef: RefObject<HTMLInputElement | null>;
  onAddMedia: (files: FileList | null) => void;
  onMoveMedia: (index: number, offset: number) => void;
  onRemoveMedia: (index: number) => void;
  mirrorToMax: boolean;
};

type MediaAttachmentEditorProps = Pick<
  AppearanceEditorProps,
  | "mediaItems"
  | "mediaBusy"
  | "mediaError"
  | "mediaInputRef"
  | "onAddMedia"
  | "onMoveMedia"
  | "onRemoveMedia"
>;

const formatBytes = (size: number) => {
  if (size < 1_024) return `${size} Б`;
  if (size < 1_048_576) return `${Math.round(size / 1_024)} КБ`;
  return `${(size / 1_048_576).toFixed(size < 10_485_760 ? 1 : 0)} МБ`;
};

const mediaTypeLabel = (media: UploadedMedia) =>
  media.mediaType === "photo" ? "Фото" : media.mediaType === "video" ? "Видео" : "Документ";

export function MediaAttachmentEditor({
  mediaItems,
  mediaBusy,
  mediaError,
  mediaInputRef,
  onAddMedia,
  onMoveMedia,
  onRemoveMedia
}: MediaAttachmentEditorProps) {
  return (
    <section className="media-drop" aria-labelledby="appearance-media-title">
      <strong id="appearance-media-title">Медиа</strong>
      <p>До 10 файлов: фото до 10 МБ, видео и документы до 50 МБ. Порядок можно изменить.</p>
      <input
        ref={mediaInputRef}
        className="visually-hidden"
        aria-label="Файлы для поста"
        type="file"
        multiple
        accept="image/jpeg,image/png,image/webp,video/*,.pdf,.doc,.docx,.xls,.xlsx"
        onChange={(event: ChangeEvent<HTMLInputElement>) => onAddMedia(event.target.files)}
      />
      <button
        className="media-add-button"
        type="button"
        disabled={mediaBusy || mediaItems.length >= 10}
        onClick={() => mediaInputRef.current?.click()}
      >
        {mediaBusy ? "Загрузка…" : "Добавить медиа"}
      </button>
      {mediaError && <p className="inline-error" role="alert">{mediaError}</p>}
      {mediaItems.length > 0 && (
        <ol className="media-list">
          {mediaItems.map((media, index) => (
            <li key={media.mediaFileId}>
              <span className="media-list-preview-frame" aria-hidden={media.mediaType !== "video" || undefined}>
                {media.localPreviewUrl && media.mediaType === "photo" && (
                  <img className="media-list-preview" src={media.localPreviewUrl} alt="" />
                )}
                {media.localPreviewUrl && media.mediaType === "video" && (
                  <>
                    <video
                      className="media-list-preview"
                      src={media.localPreviewUrl}
                      aria-label={`Локальное видео ${media.fileName}`}
                      muted
                      playsInline
                      preload="metadata"
                    />
                    <span className="media-list-video-badge" aria-hidden="true">▶</span>
                  </>
                )}
                {(!media.localPreviewUrl || media.mediaType === "document") && (
                  <span className="media-list-preview-placeholder" aria-hidden="true">
                    {media.mediaType === "video" ? "▶" : media.mediaType === "photo" ? "▣" : "📄"}
                  </span>
                )}
              </span>
              <span className="media-list-copy">
                <b>{index + 1}</b>
                <span><strong>{media.fileName}</strong><small>{mediaTypeLabel(media)} · {formatBytes(media.size)}</small></span>
                {media.mediaType === "video" && media.contentType !== "video/mp4" && (
                  <small className="inline-error">Для надёжного показа в Telegram используйте MP4 (H.264).</small>
                )}
              </span>
              <span className="media-actions">
                <button type="button" aria-label={`Поднять ${media.fileName}`} disabled={index === 0} onClick={() => onMoveMedia(index, -1)}>↑</button>
                <button type="button" aria-label={`Опустить ${media.fileName}`} disabled={index === mediaItems.length - 1} onClick={() => onMoveMedia(index, 1)}>↓</button>
                <button type="button" aria-label={`Удалить ${media.fileName}`} onClick={() => onRemoveMedia(index)}>×</button>
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

export function AppearanceEditor({
  capabilities,
  appearance,
  onAppearanceChange,
  buttonRows,
  onButtonRowsChange,
  buttonLimits,
  buttonSetStatus,
  onChooseButtonSet,
  mediaItems,
  mediaBusy,
  mediaError,
  mediaInputRef,
  onAddMedia,
  onMoveMedia,
  onRemoveMedia,
  mirrorToMax
}: AppearanceEditorProps) {
  const [confirmRichVideo, setConfirmRichVideo] = useState(false);
  const hasVideo = mediaItems.some((media) => media.mediaType === "video");
  const effectiveFormat = effectiveFormatForEditor({
    appearance,
    hasVideo,
    mirrorToMax,
    richPosts: capabilities.richPosts === true
  });
  const showRichControls = effectiveFormat === "rich";
  const richVideoNeedsAck =
    appearance.deliveryPreference === "rich" && hasVideo && !appearance.richVideoOverrideAck;
  const richVideoBlockedByMax = richVideoNeedsAck && mirrorToMax;

  useEffect(() => {
    if (!richVideoNeedsAck || richVideoBlockedByMax) setConfirmRichVideo(false);
  }, [richVideoBlockedByMax, richVideoNeedsAck]);

  const setDeliveryPreference = (deliveryPreference: CustomPostAppearance["deliveryPreference"]) => {
    setConfirmRichVideo(false);
    onAppearanceChange({
      ...appearance,
      deliveryPreference,
      richVideoOverrideAck: deliveryPreference === "rich"
        ? appearance.richVideoOverrideAck
        : false
    });
  };

  return (
    <div className="field-stack">
      <MediaAttachmentEditor
        mediaItems={mediaItems}
        mediaBusy={mediaBusy}
        mediaError={mediaError}
        mediaInputRef={mediaInputRef}
        onAddMedia={onAddMedia}
        onMoveMedia={onMoveMedia}
        onRemoveMedia={onRemoveMedia}
      />

      <section className="personal-button-set-action">
        <div><strong>Готовые кнопки</strong><small>Можно взять личный набор и затем изменить его в посте.</small></div>
        <button type="button" onClick={onChooseButtonSet}>Выбрать из моих наборов</button>
        {buttonSetStatus && <p className="save-success" role="status" aria-label="Статус набора кнопок">{buttonSetStatus}</p>}
      </section>
      <ButtonLayoutEditor
        rows={buttonRows}
        onChange={onButtonRowsChange}
        richEnabled={capabilities.richPosts === true}
        limits={buttonLimits}
      />

      <fieldset className="field-stack">
        <legend>Как уйдёт в Telegram</legend>
        {([
          ["auto", "Авто"],
          ["legacy", "Обычный"],
          ["rich", "Новый Rich — бета"]
        ] as const).map(([value, label]) => (
          <label className="choice-row" key={value}>
            <input
              type="radio"
              name="delivery-preference"
              aria-label={label}
              value={value}
              checked={appearance.deliveryPreference === value}
              disabled={value === "rich" && capabilities.richPosts !== true}
              onChange={() => setDeliveryPreference(value)}
            />
            <span>
              <strong>{label}</strong>
              {value === "auto" && <small>Обычный формат: сохраняет заданные ряды кнопок</small>}
              {value === "rich" && <small>Включается только вручную; Telegram может переносить длинные кнопки</small>}
            </span>
          </label>
        ))}
      </fieldset>

      <fieldset className="field-stack">
        <legend>Порядок текста и медиа</legend>
        <label className="choice-row"><input type="radio" name="appearance-layout" aria-label="Медиа сверху" checked={appearance.layout === "media_first"} onChange={() => onAppearanceChange({ ...appearance, layout: "media_first" })} /><span><strong>Медиа сверху</strong></span></label>
        <label className="choice-row"><input type="radio" name="appearance-layout" aria-label="Текст сверху" checked={appearance.layout === "text_first"} onChange={() => onAppearanceChange({ ...appearance, layout: "text_first" })} /><span><strong>Текст сверху</strong></span></label>
      </fieldset>

      {richVideoBlockedByMax && (
        <div className="inline-error" role="alert">При видео и зеркале в MAX доступен только обычный формат.
      </div>
      )}
      {richVideoNeedsAck && !richVideoBlockedByMax && !confirmRichVideo && (
        <div className="inline-error" role="alert">
          <p>На части iPhone видео в новом формате может показываться белым экраном.</p>
          <button type="button" onClick={() => setDeliveryPreference("legacy")}>Использовать обычный формат</button>
          <button type="button" onClick={() => setConfirmRichVideo(true)}>Всё равно новый формат</button>
        </div>
      )}
      {confirmRichVideo && (
        <div className="preview-card" role="dialog" aria-modal="true" aria-labelledby="rich-video-confirm-title">
          <strong id="rich-video-confirm-title">Видео может не отобразиться у части получателей. Отправить новым форматом?</strong>
          <button className="primary-button" type="button" onClick={() => setDeliveryPreference("legacy")}>Использовать обычный формат</button>
          <button type="button" onClick={() => { setConfirmRichVideo(false); onAppearanceChange({ ...appearance, richVideoOverrideAck: true }); }}>Отправить новым форматом</button>
        </div>
      )}

      {showRichControls && (
        <fieldset className="field-stack">
          <legend>Выравнивание Rich-кнопок</legend>
          {([
            ["left", "Слева"],
            ["center", "По центру"],
            ["right", "Справа"]
          ] as const).map(([value, label]) => (
            <label className="choice-row" key={value}><input type="radio" name="button-alignment" aria-label={label} checked={appearance.buttonAlignment === value} onChange={() => onAppearanceChange({ ...appearance, buttonAlignment: value })} /><span><strong>{label}</strong></span></label>
          ))}
        </fieldset>
      )}
    </div>
  );
}
